// src/contexts/AuthContext.tsx - Fixed with localStorage persistence
import { supabase } from '@/lib/supabase';
import type { AuthContextValue, AuthResponse, SignUpAdditionalData } from '@/types/auth';
import type { Profile } from '@/types/database';
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

const AuthContext = createContext<AuthContextValue>({
    user: null,
    profile: null,
    session: null,
    loading: true,
    profileLoaded: false,
    signUp: async () => ({ data: null, error: null }),
    signIn: async () => ({ data: null, error: null }),
    signOut: async () => ({ error: null }),
    isAdmin: false,
    isOperator: false,
    isAuthenticated: false,
    userRole: null,
    userName: null,
});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export interface AuthProviderProps {
    children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [profileLoaded, setProfileLoaded] = useState(false);
    const profileLoadedRef = useRef(false);

    // Keep ref in sync with state
    useEffect(() => {
        profileLoadedRef.current = profileLoaded;
    }, [profileLoaded]);

    // Load cached profile from localStorage on mount
    useEffect(() => {
        try {
            const cachedProfile = localStorage.getItem('fastmag_profile');
            if (cachedProfile) {
                const parsed = JSON.parse(cachedProfile) as Profile;
                console.log('📦 Loaded cached profile from localStorage:', parsed.email);
                setProfile(parsed);
                setProfileLoaded(true);
            }
        } catch (error) {
            console.error('Error loading cached profile:', error);
            localStorage.removeItem('fastmag_profile');
        }
    }, []);

    // Save profile to localStorage whenever it changes
    useEffect(() => {
        if (profile) {
            try {
                localStorage.setItem('fastmag_profile', JSON.stringify(profile));
                console.log('💾 Saved profile to localStorage:', profile.email);
            } catch (error) {
                console.error('Error saving profile to localStorage:', error);
            }
        } else {
            localStorage.removeItem('fastmag_profile');
            console.log('🗑️ Removed profile from localStorage');
        }
    }, [profile]);

    useEffect(() => {
        let isMounted = true;

        // Get initial session
        const getSession = async () => {
            try {
                const { data: { session }, error } = await supabase.auth.getSession();
                if (error) {
                    console.error('Error getting session:', error);
                    if (isMounted) {
                        setLoading(false);
                        setIsInitialized(true);
                    }
                    return;
                }

                if (isMounted) {
                    setSession(session);
                    setUser(session?.user || null);

                    if (session?.user) {
                        await fetchUserProfile(session.user.id);
                    } else {
                        setLoading(false);
                    }
                    setIsInitialized(true);
                }
            } catch (error) {
                console.error('Error in getSession:', error);
                if (isMounted) {
                    setLoading(false);
                    setIsInitialized(true);
                }
            }
        };

        getSession();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event: AuthChangeEvent, session: Session | null) => {
                console.log('Auth state change:', event, session?.user?.email);

                if (!isMounted) return;

                // CRITICAL FIX: Ignore INITIAL_SESSION events after initialization
                if (event === 'INITIAL_SESSION' && isInitialized) {
                    console.log('🚫 Ignoring INITIAL_SESSION - already initialized');
                    return;
                }

                setSession(session);
                setUser(session?.user || null);

                if (event === 'SIGNED_IN' && session?.user) {
                    console.log('User signed in, fetching profile for:', session.user.id);
                    await fetchUserProfile(session.user.id);
                } else if (event === 'TOKEN_REFRESHED' && session?.user) {
                    console.log('Token refreshed, keeping existing state');
                    // Only refetch if we don't have a profile yet (checking Ref to avoid stale closure)
                    if (!profileLoadedRef.current) {
                        console.log('No profile loaded yet, fetching...');
                        await fetchUserProfile(session.user.id);
                    } else {
                        setLoading(false);
                    }
                } else if (event === 'SIGNED_OUT') {
                    console.log('User signed out, clearing profile');
                    setProfile(null);
                    setProfileLoaded(false);
                    setLoading(false);
                } else if (event === 'INITIAL_SESSION' && session?.user && !isInitialized) {
                    console.log('Initial session with user, fetching profile...');
                    await fetchUserProfile(session.user.id);
                } else {
                    setLoading(false);
                }
            }
        );

        return () => {
            isMounted = false;
            subscription?.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // FIXED: Fetch user profile with timeout and profileLoaded flag
    const fetchUserProfile = async (userId: string) => {
        console.log('🔵 START: fetchUserProfile called for user:', userId);

        try {
            console.log('🔵 Querying profiles table...');

            const queryPromise = supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Query timeout after 5 seconds')), 5000)
            );

            const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

            console.log('🔵 Query result - data:', data);
            console.log('🔵 Query result - error:', error);

            if (error) {
                if (error.code === 'PGRST116') {
                    console.log('🔴 No profile found, creating default profile...');
                    await createDefaultProfile(userId);
                    return;
                }
                throw error;
            }

            if (data) {
                console.log('🟢 Profile data found:', data.email);
                setProfile(data as Profile);
                setProfileLoaded(true);
                console.log('🟢 Profile state set successfully');
            } else {
                console.log('🟡 No data returned, creating default profile...');
                await createDefaultProfile(userId);
                return;
            }

        } catch (error) {
            console.error('🔴 Error in fetchUserProfile:', error);

            // If we have a cached profile with data, keep using it
            const cachedProfileStr = localStorage.getItem('fastmag_profile');
            if (cachedProfileStr) {
                try {
                    const cachedProfile = JSON.parse(cachedProfileStr) as Profile;
                    if (cachedProfile && cachedProfile.email) {
                        console.log('🟡 Using cached profile from localStorage during error:', cachedProfile.email);
                        setProfile(cachedProfile);
                        setProfileLoaded(true);
                        return;
                    }
                } catch (e) {
                    console.error('🔴 Failed to parse cached profile:', e);
                }
            }

            // If we have current profile with data, keep using it
            if (profile && profile.email && profileLoaded) {
                console.log('🟡 Keeping current valid profile during error:', profile.email);
                return;
            }

            // Last resort: create emergency profile but WITHOUT is_active flag
            // This prevents RequireActive from showing "pending activation"
            console.log('🔴 Creating minimal emergency profile...');
            const emergencyProfile: Partial<Profile> = {
                id: userId,
                email: '', // Empty email signals this is an emergency profile
                role: 'registrato',
                first_name: '',
                last_name: ''
                // NO is_active field - let RequireActive handle missing profiles differently
            };
            setProfile(emergencyProfile as Profile);
            setProfileLoaded(true);
        } finally {
            console.log('🔵 Setting loading to false');
            setLoading(false);
        }
    };

    // Create default profile
    const createDefaultProfile = async (userId: string) => {
        console.log('🟡 START: createDefaultProfile for user:', userId);

        try {
            const { data: userData } = await supabase.auth.getUser();
            const email = userData?.user?.email;

            if (!email) {
                console.error('🔴 No email found for user');
                const fallbackProfile: Partial<Profile> = {
                    id: userId,
                    email: '',
                    role: 'registrato',
                    first_name: '',
                    last_name: '',
                    is_active: false
                };
                setProfile(fallbackProfile as Profile);
                setProfileLoaded(true);
                setLoading(false);
                return;
            }

            const { data, error } = await supabase
                .from('profiles')
                .insert({
                    id: userId,
                    email: email,
                    role: 'registrato',
                    first_name: '',
                    last_name: '',
                    is_active: false
                })
                .select()
                .single();

            if (error) {
                console.error('🔴 Error creating profile:', error);
                const fallbackProfile: Partial<Profile> = {
                    id: userId,
                    email: email,
                    role: 'registrato',
                    first_name: '',
                    last_name: '',
                    is_active: false
                };
                setProfile(fallbackProfile as Profile);
                setProfileLoaded(true);
            } else {
                console.log('🟢 Profile created successfully:', data);
                setProfile(data as Profile);
                setProfileLoaded(true);
            }

        } catch (error) {
            console.error('🔴 Error in createDefaultProfile:', error);
            const { data: userData } = await supabase.auth.getUser();
            const finalFallback: Partial<Profile> = {
                id: userId,
                email: userData?.user?.email || '',
                role: 'registrato',
                first_name: '',
                last_name: '',
                is_active: false
            };
            setProfile(finalFallback as Profile);
            setProfileLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    // Sign up new user
    const signUp = async (email: string, password: string, additionalData: SignUpAdditionalData = {}): Promise<AuthResponse> => {
        try {
            setLoading(true);

            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        first_name: additionalData.first_name || '',
                        last_name: additionalData.last_name || ''
                    }
                }
            });

            if (error) throw error;

            if (data.user && !data.user.email_confirmed_at && additionalData.first_name) {
                console.log('User needs email confirmation');
            } else if (data.user && data.user.email_confirmed_at && additionalData.first_name) {
                await updateProfileAfterSignup(data.user.id, additionalData);
            }

            return { data, error: null };
        } catch (error) {
            console.error('Sign up error:', error);
            return { data: null, error: error as Error };
        } finally {
            setLoading(false);
        }
    };

    // Helper function to update profile after signup
    const updateProfileAfterSignup = async (userId: string, additionalData: SignUpAdditionalData) => {
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));

            const { error } = await supabase
                .rpc('update_user_profile', {
                    user_id: userId,
                    new_first_name: additionalData.first_name,
                    new_last_name: additionalData.last_name
                });

            if (error) {
                const { error: directError } = await supabase
                    .from('profiles')
                    .update({
                        first_name: additionalData.first_name,
                        last_name: additionalData.last_name,
                    })
                    .eq('id', userId);

                if (directError) {
                    console.error('Error with direct profile update:', directError);
                }
            }
        } catch (updateErr) {
            console.error('Error in updateProfileAfterSignup:', updateErr);
        }
    };

    // Sign in existing user
    const signIn = async (email: string, password: string): Promise<AuthResponse> => {
        try {
            setLoading(true);

            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            return { data, error: null };
        } catch (error) {
            console.error('Sign in error:', error);
            return { data: null, error: error as Error };
        } finally {
            setLoading(false);
        }
    };

    // Sign out user
    const signOut = async () => {
        try {
            console.log('Signing out user...');
            setLoading(true);

            const { error } = await supabase.auth.signOut();

            if (error) {
                console.error('Supabase signOut error:', error);
            }

            setUser(null);
            setProfile(null);
            setSession(null);
            setProfileLoaded(false);

            return { error: null };
        } catch (error) {
            console.error('Sign out error:', error);
            setUser(null);
            setProfile(null);
            setSession(null);
            setProfileLoaded(false);
            return { error: error as Error };
        } finally {
            setLoading(false);
        }
    };

    // Helper functions to check user roles
    const isAdmin = profile?.role === 'admin';
    const isOperator = profile?.role === 'operator';

    const value: AuthContextValue = {
        user,
        profile,
        session,
        loading,
        profileLoaded,
        signUp,
        signIn,
        signOut,
        isAdmin,
        isOperator,
        isAuthenticated: !!user,
        userRole: profile?.role || null,
        userName: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email : null,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
